// SPDX-License-Identifier: GPL-3.0-or-later
import type { MaterializationRequest, MaterializedPages } from "./multikey-materializer";
import {
  MULTIKEY_MAX_INPUT_BYTES,
  MULTIKEY_WORKER_PROTOCOL_VERSION,
  MULTIKEY_MATERIALIZER_PROFILE,
} from "./multikey-worker-protocol";
import type {
  MultikeyRangeMetadata,
  MultikeyWorkerReply,
  MultikeyWorkerRequest,
} from "./multikey-worker-protocol";

/** Minimal injectable browser worker transport. */
export type MultikeyWorkerPort = Pick<
  Worker,
  "addEventListener" | "removeEventListener" | "postMessage" | "terminate"
>;
export interface MultikeyWorkerClientOptions {
  readonly signal?: AbortSignal;
  readonly workerUrl?: string | URL;
  readonly workerFactory?: (url: string | URL, options: WorkerOptions) => MultikeyWorkerPort;
}
/** Immutable source backing is owned by the caller, not cached by the client. */
export interface MultikeyRangeSource extends MultikeyRangeMetadata {
  readonly blob: Blob;
}
const cancelled = (): Error => new DOMException("Multikey work cancelled", "AbortError");

/** Serialized range jobs. Each job ends its physical worker, releasing bytes and parser graphs. */
export class MultikeyWorkerClient {
  private worker?: MultikeyWorkerPort;
  private generation = 0;
  private nextId = 0;
  private pending?: {
    id: number;
    resolve: (reply: MultikeyWorkerReply) => void;
    reject: (error: unknown) => void;
  };
  private tail: Promise<unknown> = Promise.resolve();
  private removeListeners?: () => void;
  private disposed = false;
  private sourceBytes = 0;
  private starts = 0;
  private conversions = 0;
  private readonly onAbort = (): void => this.dispose();

  private constructor(private readonly options: MultikeyWorkerClientOptions) {
    this.check();
    options.signal?.addEventListener("abort", this.onAbort, { once: true });
  }

  /** Creates a lazy client; cache hits need never start a worker or load a PDF. */
  public static create(options: MultikeyWorkerClientOptions = {}): MultikeyWorkerClient {
    return new MultikeyWorkerClient({
      signal: options.signal,
      workerUrl: options.workerUrl,
      workerFactory: options.workerFactory,
    });
  }

  /** Logical transport state, not a browser physical-memory acknowledgment. */
  public get status(): "ready" | "restart-required" | "disposed" {
    return this.disposed ? "disposed" : this.worker ? "ready" : "restart-required";
  }

  /** Active transferred input bytes and actual worker/conversion admissions, not heap estimates. */
  public get diagnostics(): { sourceBytes: number; workerStarts: number; conversions: number } {
    return {
      sourceBytes: this.sourceBytes,
      workerStarts: this.starts,
      conversions: this.conversions,
    };
  }

  private check(signal?: AbortSignal): void {
    if (this.disposed || this.options.signal?.aborted || signal?.aborted) throw cancelled();
  }

  private stop(error: unknown): void {
    const worker = this.worker;
    this.worker = undefined;
    this.generation++;
    this.sourceBytes = 0;
    this.removeListeners?.();
    this.removeListeners = undefined;
    const pending = this.pending;
    this.pending = undefined;
    try {
      worker?.terminate();
    } catch {
      // Revocation must still settle the RPC if a test/host transport throws.
    } finally {
      pending?.reject(error);
    }
  }

  private async initialize(signal?: AbortSignal): Promise<void> {
    this.check(signal);
    const generation = ++this.generation;
    const worker = (this.options.workerFactory ?? ((url, options) => new Worker(url, options)))(
      this.options.workerUrl ?? new URL("./multikey-worker.js", import.meta.url),
      { type: "module" },
    );
    this.worker = worker;
    this.starts++;
    const message = (event: MessageEvent<MultikeyWorkerReply>): void => {
      const reply = event.data;
      if (
        worker !== this.worker ||
        generation !== this.generation ||
        !reply ||
        reply.generation !== generation ||
        reply.id !== this.pending?.id
      )
        return;
      const pending = this.pending!;
      this.pending = undefined;
      if (reply.ok) pending.resolve(reply);
      else pending.reject(new Error(reply.error));
    };
    const error = (): void => {
      if (worker === this.worker) this.stop(new Error("Multikey worker transport failure"));
    };
    worker.addEventListener("message", message as EventListener);
    worker.addEventListener("error", error);
    worker.addEventListener("messageerror", error);
    this.removeListeners = () => {
      worker.removeEventListener("message", message as EventListener);
      worker.removeEventListener("error", error);
      worker.removeEventListener("messageerror", error);
    };
    const reply = await this.rpc({ type: "init", id: ++this.nextId, generation }, [], signal);
    this.check(signal);
    if (
      !reply.ok ||
      reply.type !== "init" ||
      reply.protocolVersion !== MULTIKEY_WORKER_PROTOCOL_VERSION ||
      reply.profile !== MULTIKEY_MATERIALIZER_PROFILE
    )
      throw new Error(
        `Multikey worker compatibility mismatch: expected protocol ${MULTIKEY_WORKER_PROTOCOL_VERSION}, profile ${MULTIKEY_MATERIALIZER_PROFILE}`,
      );
  }

  private rpc(
    message: MultikeyWorkerRequest,
    transfer: Transferable[],
    signal?: AbortSignal,
  ): Promise<MultikeyWorkerReply> {
    this.check(signal);
    const abort = (): void => this.stop(cancelled());
    return new Promise<MultikeyWorkerReply>((resolve, reject) => {
      this.pending = { id: message.id, resolve, reject };
      signal?.addEventListener("abort", abort, { once: true });
      try {
        this.worker!.postMessage(message, transfer);
      } catch (error) {
        this.stop(error);
      }
    }).finally(() => signal?.removeEventListener("abort", abort));
  }

  /** Materializes global pages from one bounded Blob. Metadata/requests are snapshotted immediately. */
  public convertRange(
    source: MultikeyRangeSource,
    request: MaterializationRequest,
    options: { signal?: AbortSignal } = {},
  ): Promise<MaterializedPages> {
    const signal = options.signal;
    const blob = source.blob;
    const range: MultikeyRangeMetadata = {
      identity: source.identity,
      byteLength: source.byteLength,
      firstPage: source.firstPage,
      geometry: source.geometry.map(g => ({
        ...g,
        view: [...g.view] as [number, number, number, number],
      })),
      occurrences: source.occurrences.map(o => ({
        ...o,
        layerPageIndices: [...o.layerPageIndices],
        keyLabels: o.keyLabels && [...o.keyLabels],
        anchor: o.anchor && { ...o.anchor },
      })),
    };
    const snapshot = {
      pages: [...request.pages],
      selections: request.selections.map(s => ({ ...s })),
    };
    const job = this.tail.then(async () => {
      this.check(signal);
      if (
        !Number.isSafeInteger(range.byteLength) ||
        range.byteLength < 8 ||
        range.byteLength > MULTIKEY_MAX_INPUT_BYTES ||
        blob.size !== range.byteLength ||
        !/^[a-f0-9]{64}$/.test(range.identity)
      )
        throw new Error("Multikey range byte length or identity invalid");
      const abort = (): void => this.stop(cancelled());
      signal?.addEventListener("abort", abort, { once: true });
      try {
        await this.initialize(signal);
        const generation = this.generation;
        // Blob is immutable; this is the sole full-size materialization and is transferred directly.
        const data = await blob.arrayBuffer();
        this.check(signal);
        if (generation !== this.generation || !this.worker)
          throw new Error("Multikey worker lifetime ended before conversion");
        this.sourceBytes = data.byteLength;
        this.conversions++;
        const reply = await this.rpc(
          { type: "convert", data, range, request: snapshot, id: ++this.nextId, generation },
          [data],
          signal,
        );
        this.check(signal);
        if (generation !== this.generation || !reply.ok || reply.type !== "convert")
          throw new Error("Invalid conversion response");
        return reply.result;
      } finally {
        signal?.removeEventListener("abort", abort);
        this.stop(cancelled());
      }
    });
    this.tail = job.then(
      () => {},
      () => {},
    );
    return job;
  }

  /** Revokes authority and terminates the active range job; idempotent. */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.options.signal?.removeEventListener("abort", this.onAbort);
    this.stop(cancelled());
  }
}
